// sound.js - Audio management system

export class SoundManager {
    constructor() {
        this.audioContext = null;
        this.sounds = new Map();
        this.music = new Map();
        this.currentMusic = null;
        this.musicVolume = 0.7;
        this.sfxVolume = 0.7;
        this.musicEnabled = true;
        this.sfxEnabled = true;
        this.initialized = false;
    }

    // Initialize audio context (call after user interaction)
    async init() {
        if (this.initialized) return;

        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.initialized = true;
            console.log('Audio system initialized');
        } catch (error) {
            console.error('Failed to initialize audio:', error);
        }
    }

    // Load a sound effect
    async loadSound(name, url) {
        if (!this.audioContext) await this.init();

        try {
            const response = await fetch(url);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
            this.sounds.set(name, audioBuffer);
            return true;
        } catch (error) {
            console.warn(`Failed to load sound ${name}:`, error);
            return false;
        }
    }

    // Load a music track
    async loadMusic(name, url) {
        if (!this.audioContext) await this.init();

        try {
            const response = await fetch(url);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
            this.music.set(name, audioBuffer);
            return true;
        } catch (error) {
            console.warn(`Failed to load music ${name}:`, error);
            return false;
        }
    }

    // Play a sound effect
    playSound(name, volume = 1.0) {
        if (!this.sfxEnabled || !this.audioContext) return null;

        const buffer = this.sounds.get(name);
        if (!buffer) {
            console.warn(`Sound ${name} not loaded`);
            return null;
        }

        try {
            const source = this.audioContext.createBufferSource();
            const gainNode = this.audioContext.createGain();

            source.buffer = buffer;
            source.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            gainNode.gain.value = this.sfxVolume * volume;
            source.start(0);

            return source;
        } catch (error) {
            console.error('Failed to play sound:', error);
            return null;
        }
    }

    // Play background music (looping)
    playMusic(name, fadeIn = true) {
        if (!this.musicEnabled || !this.audioContext) return;

        const buffer = this.music.get(name);
        if (!buffer) {
            console.warn(`Music ${name} not loaded`);
            return;
        }

        // Stop current music if playing
        this.stopMusic(fadeIn);

        try {
            const source = this.audioContext.createBufferSource();
            const gainNode = this.audioContext.createGain();

            source.buffer = buffer;
            source.loop = true;
            source.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            if (fadeIn) {
                gainNode.gain.value = 0;
                gainNode.gain.linearRampToValueAtTime(
                    this.musicVolume,
                    this.audioContext.currentTime + 1.0
                );
            } else {
                gainNode.gain.value = this.musicVolume;
            }

            source.start(0);

            this.currentMusic = { source, gainNode, name };
        } catch (error) {
            console.error('Failed to play music:', error);
        }
    }

    // Stop current music
    stopMusic(fadeOut = true) {
        if (!this.currentMusic) return;

        try {
            if (fadeOut && this.audioContext) {
                this.currentMusic.gainNode.gain.linearRampToValueAtTime(
                    0,
                    this.audioContext.currentTime + 0.5
                );
                setTimeout(() => {
                    if (this.currentMusic) {
                        this.currentMusic.source.stop();
                        this.currentMusic = null;
                    }
                }, 500);
            } else {
                this.currentMusic.source.stop();
                this.currentMusic = null;
            }
        } catch (error) {
            console.error('Failed to stop music:', error);
            this.currentMusic = null;
        }
    }

    // Set music volume
    setMusicVolume(volume) {
        this.musicVolume = Math.max(0, Math.min(1, volume));
        if (this.currentMusic) {
            this.currentMusic.gainNode.gain.value = this.musicVolume;
        }
    }

    // Set SFX volume
    setSFXVolume(volume) {
        this.sfxVolume = Math.max(0, Math.min(1, volume));
    }

    // Toggle music
    toggleMusic(enabled) {
        this.musicEnabled = enabled;
        if (!enabled && this.currentMusic) {
            this.stopMusic(true);
        }
    }

    // Toggle SFX
    toggleSFX(enabled) {
        this.sfxEnabled = enabled;
    }

    // Generate simple beep sound (for testing/placeholder)
    generateBeep(frequency = 440, duration = 0.1, volume = 0.3) {
        if (!this.sfxEnabled || !this.audioContext) return;

        try {
            const oscillator = this.audioContext.createOscillator();
            const gainNode = this.audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            oscillator.frequency.value = frequency;
            oscillator.type = 'square'; // 8-bit style square wave

            gainNode.gain.value = this.sfxVolume * volume;
            gainNode.gain.exponentialRampToValueAtTime(
                0.01,
                this.audioContext.currentTime + duration
            );

            oscillator.start(this.audioContext.currentTime);
            oscillator.stop(this.audioContext.currentTime + duration);
        } catch (error) {
            console.error('Failed to generate beep:', error);
        }
    }

    // Generate chiptune-style sound effects
    playTapSound() {
        this.generateBeep(800, 0.05, 0.2);
    }

    playSuccessSound() {
        if (!this.sfxEnabled || !this.audioContext) return;

        // Ascending arpeggio
        const notes = [523, 659, 784, 1047]; // C, E, G, C
        notes.forEach((freq, i) => {
            setTimeout(() => {
                this.generateBeep(freq, 0.1, 0.25);
            }, i * 80);
        });
    }

    playFailSound() {
        if (!this.sfxEnabled || !this.audioContext) return;

        // Descending sad notes
        const notes = [440, 392, 349]; // A, G, F
        notes.forEach((freq, i) => {
            setTimeout(() => {
                this.generateBeep(freq, 0.15, 0.25);
            }, i * 100);
        });
    }

    playNotificationSound() {
        if (!this.sfxEnabled || !this.audioContext) return;

        // Two-tone notification
        this.generateBeep(660, 0.08, 0.2);
        setTimeout(() => {
            this.generateBeep(880, 0.08, 0.2);
        }, 100);
    }

    playTaskStartSound() {
        if (!this.sfxEnabled || !this.audioContext) return;

        // Dramatic fanfare
        const notes = [392, 523, 659]; // G, C, E
        notes.forEach((freq, i) => {
            setTimeout(() => {
                this.generateBeep(freq, 0.12, 0.3);
            }, i * 60);
        });
    }

    // Preload placeholder sounds (for development without audio files)
    loadPlaceholderSounds() {
        console.log('Using generated sound effects (no audio files loaded)');
        // Generated sounds are created on-the-fly, no preloading needed
    }

    // Load actual audio files (when available)
    async loadAllAssets() {
        const soundPromises = [];

        // SFX
        const sfxFiles = [
            'tap',
            'success',
            'fail',
            'notification',
            'task_start',
            'task_end',
            'menu_select',
            'wellness_low'
        ];

        for (const name of sfxFiles) {
            soundPromises.push(
                this.loadSound(name, `src/assets/audio/sfx/${name}.mp3`)
                    .catch(() => console.log(`Using generated sound for ${name}`))
            );
        }

        // Music tracks
        const musicFiles = [
            'menu',
            'kitchen',
            'hallway',
            'lab',
            'study',
            'caravan',
            'dome',
            'shed',
            'lawn'
        ];

        for (const name of musicFiles) {
            soundPromises.push(
                this.loadMusic(name, `src/assets/audio/music/${name}.mp3`)
                    .catch(() => console.log(`No music file for ${name}`))
            );
        }

        await Promise.allSettled(soundPromises);
        console.log('Audio assets loaded (or using fallbacks)');
    }
}

// Create singleton instance
export const sound = new SoundManager();
